import { FC, ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { CryptoRewardsPanePreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsPanePreset/CryptoRewardsPanePreset'
import { getOrderPreviewOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/orderState'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { CryptoSellPreviewOrderSuccessResult, PreviewOrderResultWithStatus } from 'components/features/rewards/rewardsPane/types/orderPreviewResult'
import { formatUSCurrency } from 'helpers/formatHelpers'
import {
  getCryptoRewardsPaneFieldsStateSellModeOrNullSelector,
  getCryptoRewardsPaneProgressPercentsStateSelector,
  getCryptoRewardsPaneStateSellModeOrNullSelector,
} from 'components/features/rewards/rewardsPane/store/selectors/paneState'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { CryptoRewardsSellModeFields, CryptoRewardsSellModeSteps } from 'components/features/rewards/rewardsPane/types/paneStateSellMode'
import { SellCryptoAction } from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { CRYPTO_REWARDS_SELL_MODE_STEP_PREVIEW_ORDER, CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT } from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { useCryptoRewardsPaneModifyStateCallback } from 'components/features/rewards/rewardsPane/hooks/useCryptoRewardsPaneModifyFieldsStateCallback'
import { FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { TooltipIcon } from 'components/atoms/TooltipIcon/TooltipIcon'

import styles from 'components/features/rewards/rewardsPane/containers/CryptoRewardsSellPane/CryptoRewardsSellPreviewOrderStep/CryptoRewardsSellPreviewOrderStep.styl'
import { useGetCryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencyNameById'


type CryptoRewardsSellPreviewOrderStepProps = {
  onClose: () => void
}

type CryptoRewardsSellPreviewOrderProps = CryptoRewardsSellPreviewOrderStepProps & {
  previewOrderSuccessResult: CryptoSellPreviewOrderSuccessResult
  onFooterCancel: () => void
}

export const CryptoRewardsSellPreviewOrder: FC<CryptoRewardsSellPreviewOrderProps> = ({
  previewOrderSuccessResult: previewOrderResult,
  onClose,
  onFooterCancel,
}): ReactElement => {
  const dispatch = useStoreDispatch()

  const paneFieldsStateOrNull: Partial<CryptoRewardsSellModeFields> | null = useStoreSelector(getCryptoRewardsPaneFieldsStateSellModeOrNullSelector)
  const paneProgressPercent = useStoreSelector(getCryptoRewardsPaneProgressPercentsStateSelector)

  const {
    amountToSellUSD,
    taxesUSD,
    feesUSD,
    estRewardsUSD,
  } = previewOrderResult

  const renderOrderPreviewRow = useCallback((
    fieldLabel: string,
    fieldValue: number,
    helpIcon?: ReactNode
  ): ReactElement => (
    <li key={ fieldLabel } className={ styles.listRow }>
      <h6 className={ styles.fieldLabel } >{ fieldLabel } { helpIcon && <span className={ styles.helpIcon }>{ helpIcon }</span> }</h6>
      <p className={ styles.fieldValue }>{ formatUSCurrency(fieldValue, 'penny') }</p>
    </li>
  ), [])
  const renderHintIcon = useCallback((
    hint?: ReactNode
  ): ReactElement => (
    <TooltipIcon content={ hint } />
  ), [])

  const amountToSellRowNode = useMemo(() => {
    const hintIcon = renderHintIcon(
      'We’ll sell crypto equivalent to this amount at the current market rate. The actual sale amount may vary due to market volatility.'
    )
    return renderOrderPreviewRow('Est. Price', amountToSellUSD, hintIcon)
  }, [amountToSellUSD, renderOrderPreviewRow, renderHintIcon])
  const estTaxesRowNode = useMemo(() => {
    return renderOrderPreviewRow('Est. Taxes', taxesUSD)
  }, [taxesUSD, renderOrderPreviewRow])
  const feesRowNode = useMemo(() => {
    const hintIcon = renderHintIcon(
      'These fees cover crypto transaction costs for Status Money.'
    )
    return renderOrderPreviewRow('Est. Fees', feesUSD, hintIcon)
  }, [feesUSD, renderOrderPreviewRow, renderHintIcon])
  const estRewardsNode = useMemo(() => {
    const hintIcon = renderHintIcon(
      'The amount you receive may be different due to market volatility.'
    )
    return renderOrderPreviewRow('Est. Rewards', estRewardsUSD, hintIcon)
  }, [estRewardsUSD, renderOrderPreviewRow, renderHintIcon])
  const listNode = useMemo((): ReactElement => (
    <ul className={ styles.list }>
      { amountToSellRowNode }
      { estTaxesRowNode }
      { feesRowNode }
      { estRewardsNode }
    </ul>
  ), [amountToSellRowNode, estTaxesRowNode, feesRowNode, estRewardsNode])

  const cryptoCurrencyIDChosenForSelling = paneFieldsStateOrNull?.cryptoCurrencyIDChosenForSelling
  const sellingAmountUSD = paneFieldsStateOrNull?.sellingAmountUSD
  const sellingAmountCrypto = paneFieldsStateOrNull?.sellingAmountCrypto

  const handleMoveNextStep = useCallback(() => {
    if (!sellingAmountUSD || !cryptoCurrencyIDChosenForSelling || !sellingAmountCrypto) {
      return
    }
    dispatch(new SellCryptoAction({
      step: CRYPTO_REWARDS_SELL_MODE_STEP_PREVIEW_ORDER,
      fields: {
        cryptoCurrencyIDChosenForSelling,
        sellingAmountUSD,
        sellingAmountCrypto,
        errors: [],
      },
      backStep: CRYPTO_REWARDS_SELL_MODE_STEP_SELL_AMOUNT,
      mode: 'cryptoRewardsSellMode',
    }))
  }, [cryptoCurrencyIDChosenForSelling, sellingAmountUSD, dispatch, sellingAmountCrypto])

  const isRequiredFieldsEmpty = !sellingAmountUSD || !cryptoCurrencyIDChosenForSelling

  const cryptoCurrencyName = useGetCryptoCurrencyNameById(cryptoCurrencyIDChosenForSelling || null)

  const mainDescriptionNode = useMemo<ReactElement>(() => (
    <small className={ styles.sellOrderNotice }>
      When you submit this request, we’ll sell { cryptoCurrencyName } equivalent to the amount you specified at the current market rate. This will
      automatically deactivate the auto-investing option if it was previously active. The sale amount will be credited to your cash
      rewards balance when the sale is completed, minus the estimated taxes and fees. Once submitted, this transaction can’t be undone.
    </small>
  ), [cryptoCurrencyName])

  return (
    <CryptoRewardsPanePreset
      title='Sell Your Crypto Investments'
      subTitle='Review your investment sale.'
      actionName='Confirm sale'
      cancelName='Back'
      actionDisabled={ isRequiredFieldsEmpty }
      onSuccess={ handleMoveNextStep }
      onCancel={ onClose }
      onFooterCancel={ onFooterCancel }
      progressBarPercent={ paneProgressPercent }
    >
      { listNode }
      { mainDescriptionNode }
    </CryptoRewardsPanePreset>
  )
}

export const CryptoRewardsSellPreviewOrderStep: FC<CryptoRewardsSellPreviewOrderStepProps> = ({ onClose }): ReactElement | null => {
  const previewOrderResult: PreviewOrderResultWithStatus | null = useStoreSelector(getOrderPreviewOrNullSelector)
  const previewOrderSuccessResultOrNull = previewOrderResult?.success ?? null
  const sellPaneStateOrNull: CryptoRewardsSellModeSteps | null = useStoreSelector(getCryptoRewardsPaneStateSellModeOrNullSelector)

  const backStep = sellPaneStateOrNull?.backStep
  const modifyPaneState = useCryptoRewardsPaneModifyStateCallback(FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE)
  const handleFooterCancelButtonPress = useCallback(() => {
    if (backStep) {
      modifyPaneState({
        step: backStep,
        backStep: undefined,
        progressPercents: 20,
      })
      return
    }
    onClose()
  }, [backStep, modifyPaneState, onClose])

  if (!previewOrderResult || !previewOrderSuccessResultOrNull) {
    return null
  }
  return (
    <CryptoRewardsSellPreviewOrder
      previewOrderSuccessResult={ previewOrderSuccessResultOrNull }
      onClose={ onClose }
      onFooterCancel={ handleFooterCancelButtonPress }
    />
  )
}
